import type { NetGame } from '@orb/net';
import { seat } from '../seat';
import { ICONS, octButton } from '../ui/kit';

/**
 * Where the game server lives: `?server=` overrides; then VITE_ORB_SERVER set at build time (the game
 * served from another domain than the server); dev talks to :8787, production to `/ws` on the same host.
 */
export function serverUrl(params: URLSearchParams): string {
  const forced = params.get('server') || import.meta.env.VITE_ORB_SERVER;
  if (forced) return forced;
  const ws = location.protocol === 'https:' ? 'wss' : 'ws';
  return import.meta.env.DEV ? `${ws}://${location.hostname}:8787/ws` : `${ws}://${location.host}/ws`;
}

type Way = { play?: 'solo' | 'coop' | 'random' | 'bot'; room?: string };
/** The address of another way of playing, keeping unrelated parameters (?server=, quality, debug flags). */
export function wayUrl(change: Way): string {
  const url = new URL(location.href);
  // `via` (an invite) belongs to the room it brought the player to, not to the next one
  for (const k of ['mode', 'play', 'room', 'seed', 'via']) url.searchParams.delete(k);
  if (change.room) url.searchParams.set('room', change.room);
  else if (change.play && change.play !== 'solo') url.searchParams.set('play', change.play);
  return url.href;
}
/** Reload into another way of playing. */
export function go(change: Way) { location.assign(wayUrl(change)); }

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, text = '') => {
  const e = document.createElement(tag); e.className = cls; if (text) e.textContent = text; return e;
};
/** Taps on the menu never reach the scene underneath. */
const shield = (e: HTMLElement) => { e.addEventListener('pointerdown', (ev) => ev.stopPropagation()); return e; };

/** Clipboard with a fallback for WebViews without the async API (some Telegram clients). */
export async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* try the old way */ }
  try {
    const t = document.createElement('textarea'); t.value = text; t.setAttribute('readonly', '');
    t.style.cssText = 'position:fixed;opacity:0;pointer-events:none'; document.body.append(t); t.select();
    const ok = document.execCommand('copy'); t.remove(); return ok;
  } catch { return false; }
}

/** How a room is shared: its link, and (in Telegram) the native «Share» picker. */
export interface Invite { link(code: string): string; share?(code: string): boolean }

/**
 * The battle's own corner: the menu button (☰ — pause, settings, rules, leaving; Esc does the same)
 * with the room's connection beside it online, and the notices of a co-op match.
 */
export function mountMenu(host: HTMLElement, o: { net: NetGame | null; onAlly?: (name: string) => void; onMenu: () => void }) {
  const top = el('div', 'menu-bar');
  top.append(shield(octButton(ICONS.menu, 'Меню · Esc', { size: 42, onClick: o.onMenu })));
  host.append(top);
  if (o.net) mountLobby(host, o.net, o.onAlly);
}

/**
 * The invite link on the web: this page with ?room=CODE, built from scratch — never from location.href,
 * whose fragment in Telegram carries the signed launch data (a day's sign-in as this player). Only the
 * server the room lives on comes along (?server=): without it the friend would look on another one.
 */
export const pageInvite = (code: string) => {
  const u = new URL(location.origin + location.pathname), server = new URLSearchParams(location.search).get('server');
  if (server) u.searchParams.set('server', server);
  u.searchParams.set('room', code);
  return u.href;
};

/**
 * Online, over the battle: a notice when the ally comes, drops out or leaves, and «match over» when
 * the server no longer holds our seat. (Waiting for the ally is the room screen, Matchmaking.ts.)
 */
function mountLobby(host: HTMLElement, net: NetGame, onAlly?: (name: string) => void) {
  const notice = el('div', 'coop-notice'); notice.setAttribute('role', 'status'); notice.hidden = true;
  host.append(notice);

  // the server no longer holds our seat: say so plainly and offer a way on
  const lost = shield(el('div', 'coop-wait')); lost.hidden = true; lost.setAttribute('role', 'alertdialog');
  const again = el('button', 'coop-primary', 'Новий кооп'); again.type = 'button';
  again.addEventListener('click', () => go({ play: 'coop' }));
  const solo = el('button', 'coop-cancel', 'Грати соло'); solo.type = 'button';
  solo.addEventListener('click', () => go({ play: 'solo' }));
  lost.append(el('div', 'coop-title', 'Матч завершено'),
    el('div', 'coop-or', 'Сервер оновився або ви були поза грою понад хвилину. Зігране збережено в прогресі.'), again, solo);
  host.append(lost);
  net.onLost = () => { notice.hidden = true; net.close(); seat.forget(); lost.hidden = false; };

  const players = new Map((net.room?.players ?? []).map((p) => [p.seat, p.connected]));
  // the ally by nick (an account without one, or an old build, shows as «Союзник»)
  const names = new Map((net.room?.players ?? []).map((p) => [p.seat, p.name]));
  const who = (seat: number) => names.get(seat) || 'Союзник';
  const ally = net.room?.players.find((p) => p.seat !== net.seat && !p.bot);
  if (ally?.name) onAlly?.(ally.name);
  const say = (text: string) => { notice.textContent = text; notice.hidden = false; setTimeout(() => (notice.hidden = true), 4500); };
  const prev = net.onMessage;
  net.onMessage = (m) => {
    prev(m);
    if (m.type === 'player_joined' && m.player.seat !== net.seat) {
      const back = players.has(m.player.seat);
      players.set(m.player.seat, true);
      if (m.player.name) { names.set(m.player.seat, m.player.name); if (!m.player.bot) onAlly?.(m.player.name); }
      say(`${who(m.player.seat)}: ${back ? 'повернення в гру' : 'приєднання до кімнати'}`);
    }
    if (m.type === 'player_left' && m.seat !== net.seat) {
      const name = who(m.seat);
      say(m.reason === 'disconnected' ? `${name} втрачає зв’язок — чекаємо до хвилини. Гра триває.`
        : m.reason === 'timeout' ? `${name} не повертається. Поле союзника воює далі.`
        : `${name} виходить із гри. Гра триває: сфери й тотем союзника воюють далі.`);
    }
  };
}
